import { useMutation } from 'Data/Api'
import {
  notifyError,
  useNotifications,
  notifySuccess,
} from 'Logic/Notifications.js'
import { useDataValue } from 'Simple/Data.js'
import { percentage } from 'Data/format.js'
import { FEE_MODE_RANGES, FEE_MODE } from 'Data/constants.js'

import mutationSaveSettings from './mutation-save-settings.graphql.js'
import mutationSetDefault from './mutation-set-default.graphql.js'
import mutationUpdateStatus from './mutation-update-status.graphql.js'

/** @type {import('Simple/types.js').useDataOnSubmit} */
export default function useDataOnSubmit(props, data, reExecuteQuery) {
  let [, notify] = useNotifications()
  let location_id = useDataValue({
    viewPath: props.viewPath,
    context: 'selected',
    path: 'id',
  })

  let vaxiom_location_id = useDataValue({
    viewPath: props.viewPath,
    context: 'selected',
    path: 'vaxiom_id',
  })

  let user_id = useDataValue({
    viewPath: props.viewPath,
    context: 'global',
    path: 'user.id',
  })

  let [, executeMutationSaveSettings] = useMutation(mutationSaveSettings)
  let [, executeMutationSetDefault] = useMutation(mutationSetDefault)
  let [, executeMutationUpdateStatus] = useMutation(mutationUpdateStatus)

  return async function onSubmit({ value, originalValue, args, change }) {
    switch (args?.type) {
      case 'change_default': {
        if (!args.account_id) {
          return
        }

        let mutationResponse = await executeMutationSetDefault({
          location_id,
          provider_location_account_id: args.account_id,
          vaxiom_location_id,
          should_insert: args.provider === 'payabli',
        })

        if (mutationResponse.error) {
          notify(
            notifyError(
              'Something went wrong. Please, try again or contact support if the problem persists.'
            )
          )
          return
        }

        notify(notifySuccess('Default provider set!'))
        return
      }

      case 'verify_paypoint':
      case 'invalidate_paypoint': {
        if (!args.provider_location_account_id || !user_id) {
          return
        }

        let mutationResponse = await executeMutationUpdateStatus({
          id: args.provider_location_account_id,
          user_id,
          status: args.type === 'verify_paypoint' ? 'active' : 'invalidated',
          is_default: args.type === 'verify_paypoint',
          location_id,
        })

        if (mutationResponse.error) {
          notify(
            notifyError(
              'Something went wrong. Please, try again or contact support if the problem persists.'
            )
          )
          return
        }

        notify(
          notifySuccess(
            args.type === 'verify_paypoint'
              ? 'Payment provider approved!'
              : 'Payment Provider rejected!'
          )
        )
        return
      }

      case 'save_settings': {
        switch (args.fee_mode) {
          case FEE_MODE.PLATFORM_FEE: {
            if (
              typeof args.card_fee_percent !== 'number' ||
              args.card_fee_percent < FEE_MODE_RANGES.PLATFORM_FEE.CARD.MIN ||
              args.card_fee_percent > FEE_MODE_RANGES.PLATFORM_FEE.CARD.MAX
            ) {
              notify(
                notifyError(
                  `The card platform fee must be a value between ${percentage(
                    FEE_MODE_RANGES.PLATFORM_FEE.CARD.MIN
                  )} and ${percentage(FEE_MODE_RANGES.PLATFORM_FEE.CARD.MAX)}.`
                )
              )
              return
            }
            if (
              typeof args.ach_fee_percent !== 'number' ||
              args.ach_fee_percent < FEE_MODE_RANGES.PLATFORM_FEE.ACH.MIN ||
              args.ach_fee_percent > FEE_MODE_RANGES.PLATFORM_FEE.ACH.MAX
            ) {
              notify(
                notifyError(
                  `The ACH platform fee must be a value between ${percentage(
                    FEE_MODE_RANGES.PLATFORM_FEE.ACH.MIN
                  )} and ${percentage(FEE_MODE_RANGES.PLATFORM_FEE.ACH.MAX)}.`
                )
              )
              return
            }
            break
          }
          case FEE_MODE.SURCHARGE: {
            if (
              typeof args.card_fee_percent !== 'number' ||
              args.card_fee_percent < FEE_MODE_RANGES.SURCHARGE.CARD.MIN ||
              args.card_fee_percent > FEE_MODE_RANGES.SURCHARGE.CARD.MAX
            ) {
              notify(
                notifyError(
                  `The surcharge must be a value between ${percentage(
                    FEE_MODE_RANGES.SURCHARGE.CARD.MIN
                  )} and ${percentage(FEE_MODE_RANGES.SURCHARGE.CARD.MAX)}.`
                )
              )
              return
            }
            break
          }
          case FEE_MODE.STANDARD:
          default:
            break
        }

        let saveSettingsMutationResponse = await executeMutationSaveSettings({
          location_id,
          ...args,
        })

        if (saveSettingsMutationResponse.error) {
          notify(
            notifyError(
              'Something went wrong. Please, try again or contact support if the problem persists.'
            )
          )
          return
        }

        notify(notifySuccess('Updated successfully'))

        return
      }

      case 'discard_changes': {
        change(next => {
          let index = next.findIndex(
            item => item.id === args.provider_account_id
          )

          if (index !== -1) {
            next[index] = originalValue[index]
          }
        })
        return
      }
      default: {
        return
      }
    }
  }
}
